import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import { fetchFleetOverview } from "../../../views/Fleet/fleetApi";
import { startOfLocalDay } from "../../Fleet/fleetFormat";
import WidgetFrame, { WidgetEmpty, WidgetStat } from "./WidgetFrame";

// Fleet widgets (Admin / Fleet Manager only, via catalog roles — the API
// also checks the x-user-id header server-side). One per Fleet page, all
// fed by the same overview call the Fleet home page uses.
const useFleetOverview = () => {
  const user = useSelector(selectUser);
  const [overview, setOverview] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    fetchFleetOverview(user.id, startOfLocalDay(0))
      .then(setOverview)
      .catch(() => setFailed(true));
  }, [user?.id]);

  return { overview, failed };
};

const FleetTile = ({ title, to, stats }) => {
  const { overview, failed } = useFleetOverview();
  return (
    <WidgetFrame title={title} to={to}>
      {overview ? (
        <div className="grid grid-cols-3 gap-2">
          {stats(overview).map(([value, label]) => (
            <WidgetStat key={label} value={value} label={label} />
          ))}
        </div>
      ) : (
        <WidgetEmpty>{failed ? "Couldn't load the fleet." : "Loading fleet…"}</WidgetEmpty>
      )}
    </WidgetFrame>
  );
};

const ratio = (part, whole) => `${part}/${whole}`;

// Fleet home: today across forklifts and trucks.
const FleetWidget = () => (
  <FleetTile
    title="Fleet"
    to="/fleet"
    stats={(o) => [
      [ratio(o.forklifts.inspectedToday, o.forklifts.inService), "Forklifts checked"],
      [o.vehicles ? ratio(o.vehicles.inspectedToday, o.vehicles.total) : "—", "Trucks inspected"],
      [o.inspections.flaggedWeek, "Flagged (7d)"],
    ]}
  />
);

export const ForkliftsWidget = () => (
  <FleetTile
    title="Forklifts"
    to="/fleet/forklifts"
    stats={(o) => [
      [ratio(o.forklifts.inspectedToday, o.forklifts.inService), "Checked today"],
      [o.forklifts.outOfService, "Out of service"],
      [o.forklifts.quiet, "No check-in 7d+"],
    ]}
  />
);

export const VehiclesWidget = () => (
  <FleetTile
    title="Vehicles"
    to="/fleet/vehicles"
    stats={(o) =>
      o.vehicles
        ? [
            [o.vehicles.running, "Running now"],
            [ratio(o.vehicles.inspectedToday, o.vehicles.total), "Inspected today"],
            [o.vehicles.total - o.vehicles.tracked, "No GPS"],
          ]
        : [["—", "Running now"], ["—", "Inspected today"], ["—", "No GPS"]]
    }
  />
);

export const InspectionLogWidget = () => (
  <FleetTile
    title="Inspection Log"
    to="/fleet/inspections"
    stats={(o) => [
      [o.inspections.today, "Today"],
      [o.inspections.week.toLocaleString(), "Last 7 days"],
      [o.inspections.flaggedWeek, "Flagged (7d)"],
    ]}
  />
);

export default FleetWidget;
